import React from 'react';
import AssetImageField from './AssetImageField';

function Field({ label, shell, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className={`text-xs font-semibold ${shell.label}`}>{label}</label>
      {children}
    </div>
  );
}

function LocalizedInput({ value, onChange, shell, multiline = false, rows = 2 }) {
  const className = `w-full border rounded-lg px-4 py-2 text-sm focus:outline-none transition-colors ${shell.input}`;
  if (multiline) {
    return <textarea rows={rows} value={value || ''} onChange={onChange} className={`${className} resize-none`} />;
  }
  return <input type="text" value={value || ''} onChange={onChange} className={className} />;
}

function SectionBlock({ title, children, shell }) {
  return (
    <div className={`flex flex-col gap-3 p-3 border rounded-lg ${shell.contentBox}`}>
      <p className={`text-[10px] font-bold uppercase tracking-wider ${shell.muted}`}>{title}</p>
      {children}
    </div>
  );
}

export default function LandingContentEditor({ content, activeLangTab, shell, onChange }) {
  const lang = activeLangTab;

  const setHero = (field, val) => {
    onChange({
      ...content,
      hero: { ...content.hero, [field]: { ...(content.hero?.[field] || {}), [lang]: val } },
    });
  };

  const setSectionLocalized = (section, field, val) => {
    onChange({
      ...content,
      [section]: {
        ...content[section],
        [field]: { ...(content[section]?.[field] || {}), [lang]: val },
      },
    });
  };

  const getBenefitItems = () => {
    const items = content.benefits?.items;
    if (items?.[lang]) return items[lang];
    if (Array.isArray(items)) return items;
    return [];
  };

  const setBenefitItem = (index, field, val) => {
    const items = content.benefits?.items || { english: [], hebrew: [] };
    const langItems = [...(items[lang] || [])];
    const current = typeof langItems[index] === 'string' ? { title: langItems[index], description: '' } : { ...langItems[index] };
    langItems[index] = { ...current, [field]: val };
    onChange({
      ...content,
      benefits: { ...content.benefits, items: { ...items, [lang]: langItems } },
    });
  };

  const addBenefitItem = () => {
    const items = content.benefits?.items || { english: [], hebrew: [] };
    if ((items[lang] || []).length >= 4) return;
    onChange({
      ...content,
      benefits: { ...content.benefits, items: { ...items, [lang]: [...(items[lang] || []), { title: '', description: '', icon: 'Sparkles' }] } },
    });
  };

  const removeBenefitItem = (index) => {
    const items = content.benefits?.items || { english: [], hebrew: [] };
    onChange({
      ...content,
      benefits: { ...content.benefits, items: { ...items, [lang]: (items[lang] || []).filter((_, i) => i !== index) } },
    });
  };

  const getFaqItems = () => {
    const items = content.faq?.items;
    if (items?.[lang]) return items[lang];
    if (Array.isArray(items)) return items;
    return [];
  };

  const setFaqItem = (index, field, val) => {
    const items = content.faq?.items || { english: [], hebrew: [] };
    const langItems = [...(items[lang] || [])];
    while (langItems.length <= index) langItems.push({ question: '', answer: '' });
    langItems[index] = { ...langItems[index], [field]: val };
    onChange({
      ...content,
      faq: { ...content.faq, items: { ...items, [lang]: langItems } },
    });
  };

  const benefitItems = getBenefitItems();
  const faqItems = getFaqItems();

  return (
    <div className="flex flex-col gap-4" dir={lang === 'hebrew' ? 'rtl' : 'ltr'}>
      <SectionBlock title="Hero" shell={shell}>
        <Field label="Badge" shell={shell}>
          <LocalizedInput value={content.hero?.badge?.[lang]} onChange={(e) => setHero('badge', e.target.value)} shell={shell} />
        </Field>
        <Field label="Title" shell={shell}>
          <LocalizedInput value={content.hero?.title?.[lang]} onChange={(e) => setHero('title', e.target.value)} shell={shell} />
        </Field>
        <Field label="Subtitle" shell={shell}>
          <LocalizedInput value={content.hero?.subtitle?.[lang]} onChange={(e) => setHero('subtitle', e.target.value)} shell={shell} />
        </Field>
        <Field label="Description" shell={shell}>
          <LocalizedInput value={content.hero?.paragraph?.[lang]} onChange={(e) => setHero('paragraph', e.target.value)} shell={shell} multiline />
        </Field>
        <Field label="CTA button" shell={shell}>
          <LocalizedInput value={content.hero?.ctaText?.[lang]} onChange={(e) => setHero('ctaText', e.target.value)} shell={shell} />
        </Field>
        <AssetImageField
          label="Hero image (optional)"
          hint="Upload a photo for the hero section"
          folder="landing"
          value={content.hero?.imageUrl || ''}
          onChange={(imageUrl) => onChange({ ...content, hero: { ...content.hero, imageUrl } })}
          shell={shell}
        />
      </SectionBlock>

      <SectionBlock title="Benefits" shell={shell}>
        <Field label="Section eyebrow" shell={shell}>
          <LocalizedInput value={content.benefits?.sectionEyebrow?.[lang]} onChange={(e) => setSectionLocalized('benefits', 'sectionEyebrow', e.target.value)} shell={shell} />
        </Field>
        <Field label="Section title" shell={shell}>
          <LocalizedInput value={content.benefits?.sectionTitle?.[lang]} onChange={(e) => setSectionLocalized('benefits', 'sectionTitle', e.target.value)} shell={shell} />
        </Field>
        <Field label="Section subtitle" shell={shell}>
          <LocalizedInput value={content.benefits?.sectionSubtitle?.[lang]} onChange={(e) => setSectionLocalized('benefits', 'sectionSubtitle', e.target.value)} shell={shell} multiline rows={2} />
        </Field>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className={`text-xs font-semibold ${shell.label}`}>Benefit items (max 4)</span>
            <button type="button" onClick={addBenefitItem} disabled={benefitItems.length >= 4} className="text-[10px] font-bold text-forest-ondark hover:text-forest disabled:opacity-30">
              + Add
            </button>
          </div>
          {benefitItems.map((item, idx) => {
            const title = typeof item === 'string' ? item : item?.title || '';
            const description = typeof item === 'string' ? '' : item?.description || '';
            return (
              <div key={idx} className="flex flex-col gap-1.5 p-2 rounded-lg border border-dashed border-line">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Title"
                    value={title}
                    onChange={(e) => setBenefitItem(idx, 'title', e.target.value)}
                    className={`flex-1 border rounded-lg px-3 py-1.5 text-xs ${shell.input}`}
                  />
                  <button type="button" onClick={() => removeBenefitItem(idx)} className="px-2 text-red-700 dark:text-red-400 text-xs">✕</button>
                </div>
                <input
                  type="text"
                  placeholder="Description"
                  value={description}
                  onChange={(e) => setBenefitItem(idx, 'description', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-1.5 text-xs ${shell.input}`}
                />
              </div>
            );
          })}
        </div>
      </SectionBlock>

      <SectionBlock title="Showcase" shell={shell}>
        <Field label="Section eyebrow" shell={shell}>
          <LocalizedInput value={content.showcase?.sectionEyebrow?.[lang]} onChange={(e) => setSectionLocalized('showcase', 'sectionEyebrow', e.target.value)} shell={shell} />
        </Field>
        <Field label="Section title" shell={shell}>
          <LocalizedInput value={content.showcase?.sectionTitle?.[lang]} onChange={(e) => setSectionLocalized('showcase', 'sectionTitle', e.target.value)} shell={shell} />
        </Field>
        <Field label="Section subtitle" shell={shell}>
          <LocalizedInput value={content.showcase?.sectionSubtitle?.[lang]} onChange={(e) => setSectionLocalized('showcase', 'sectionSubtitle', e.target.value)} shell={shell} multiline rows={2} />
        </Field>
        <AssetImageField
          label="Showcase image (optional)"
          hint="Upload a product or lifestyle image for the showcase section"
          folder="landing"
          value={content.showcase?.imageUrl || ''}
          onChange={(imageUrl) => onChange({ ...content, showcase: { ...content.showcase, imageUrl } })}
          shell={shell}
        />
      </SectionBlock>

      <SectionBlock title="Social proof" shell={shell}>
        <Field label="Section eyebrow" shell={shell}>
          <LocalizedInput value={content.socialProof?.sectionEyebrow?.[lang]} onChange={(e) => setSectionLocalized('socialProof', 'sectionEyebrow', e.target.value)} shell={shell} />
        </Field>
        <Field label="Section title" shell={shell}>
          <LocalizedInput value={content.socialProof?.sectionTitle?.[lang]} onChange={(e) => setSectionLocalized('socialProof', 'sectionTitle', e.target.value)} shell={shell} />
        </Field>
        <Field label="Section subtitle" shell={shell}>
          <LocalizedInput value={content.socialProof?.sectionSubtitle?.[lang]} onChange={(e) => setSectionLocalized('socialProof', 'sectionSubtitle', e.target.value)} shell={shell} multiline rows={2} />
        </Field>
      </SectionBlock>

      <SectionBlock title="Comparison" shell={shell}>
        <Field label="Section eyebrow" shell={shell}>
          <LocalizedInput value={content.comparison?.sectionEyebrow?.[lang]} onChange={(e) => setSectionLocalized('comparison', 'sectionEyebrow', e.target.value)} shell={shell} />
        </Field>
        <Field label="Section title" shell={shell}>
          <LocalizedInput value={content.comparison?.sectionTitle?.[lang]} onChange={(e) => setSectionLocalized('comparison', 'sectionTitle', e.target.value)} shell={shell} />
        </Field>
        <Field label="Our column label" shell={shell}>
          <LocalizedInput value={content.comparison?.ourLabel?.[lang]} onChange={(e) => setSectionLocalized('comparison', 'ourLabel', e.target.value)} shell={shell} />
        </Field>
        <Field label="Their column label" shell={shell}>
          <LocalizedInput value={content.comparison?.theirLabel?.[lang]} onChange={(e) => setSectionLocalized('comparison', 'theirLabel', e.target.value)} shell={shell} />
        </Field>
      </SectionBlock>

      <SectionBlock title="Final CTA" shell={shell}>
        <Field label="Section eyebrow" shell={shell}>
          <LocalizedInput value={content.finalCta?.sectionEyebrow?.[lang]} onChange={(e) => setSectionLocalized('finalCta', 'sectionEyebrow', e.target.value)} shell={shell} />
        </Field>
        <Field label="Title" shell={shell}>
          <LocalizedInput value={content.finalCta?.title?.[lang]} onChange={(e) => setSectionLocalized('finalCta', 'title', e.target.value)} shell={shell} />
        </Field>
        <Field label="Subtitle" shell={shell}>
          <LocalizedInput value={content.finalCta?.subtitle?.[lang]} onChange={(e) => setSectionLocalized('finalCta', 'subtitle', e.target.value)} shell={shell} multiline />
        </Field>
        <Field label="CTA button" shell={shell}>
          <LocalizedInput value={content.finalCta?.ctaText?.[lang]} onChange={(e) => setSectionLocalized('finalCta', 'ctaText', e.target.value)} shell={shell} />
        </Field>
        <Field label="Trust note" shell={shell}>
          <LocalizedInput value={content.finalCta?.trustNote?.[lang]} onChange={(e) => setSectionLocalized('finalCta', 'trustNote', e.target.value)} shell={shell} />
        </Field>
      </SectionBlock>

      <SectionBlock title="FAQ" shell={shell}>
        <Field label="Section eyebrow" shell={shell}>
          <LocalizedInput value={content.faq?.sectionEyebrow?.[lang]} onChange={(e) => setSectionLocalized('faq', 'sectionEyebrow', e.target.value)} shell={shell} />
        </Field>
        <Field label="Section title" shell={shell}>
          <LocalizedInput value={content.faq?.sectionTitle?.[lang]} onChange={(e) => setSectionLocalized('faq', 'sectionTitle', e.target.value)} shell={shell} />
        </Field>
        {faqItems.slice(0, 4).map((item, idx) => (
          <div key={idx} className="flex flex-col gap-1.5 p-2 rounded-lg border border-dashed border-line">
            <input
              type="text"
              placeholder={`Question ${idx + 1}`}
              value={item?.question || ''}
              onChange={(e) => setFaqItem(idx, 'question', e.target.value)}
              className={`w-full border rounded-lg px-3 py-1.5 text-xs ${shell.input}`}
            />
            <textarea
              rows={2}
              placeholder="Answer"
              value={item?.answer || ''}
              onChange={(e) => setFaqItem(idx, 'answer', e.target.value)}
              className={`w-full border rounded-lg px-3 py-1.5 text-xs resize-none ${shell.input}`}
            />
          </div>
        ))}
      </SectionBlock>
    </div>
  );
}
